import React, { useMemo } from "react";
import { MdRemoveShoppingCart, MdWarning } from "react-icons/md";
import { useTheme } from "../../hooks/useTheme";
import { StockStore, ItemStop } from "../../types/types";
import styles from "./StockBadge.module.scss";

type Props = {
  stockStores?: StockStore[];
  stockEnabled: boolean;
  forceStore?: boolean;
  itemStops?: ItemStop[];
  selectedStoreIds?: number[];
  small?: boolean;
};

export const StockBadge = ({
  stockStores,
  stockEnabled,
  forceStore = false,
  itemStops = [],
  selectedStoreIds = [],
  small = false,
}: Props) => {
  const { theme } = useTheme();

  const { status } = useMemo(() => {
    // Принудительный выбор точки
    if (forceStore && selectedStoreIds.length > 0) {
      const selectedStoreId = selectedStoreIds[0];

      if (stockEnabled) {
        const stock = stockStores?.find((s) => s.store?.id === selectedStoreId);
        const available = stock ? stock.quantity - stock.reserved > 0 : false;
        if (!available) return { status: "unavailable" };
        return { status: "available" };
      }

      const stop = itemStops?.find((s) => s.storeId === selectedStoreId);
      const available = !stop?.isStopped;
      if (!available) return { status: "unavailable" };
      return { status: "available" };
    }

    // Обычный режим — показываем только limited, и только если точек > 1
    if (stockEnabled) {
      if (!stockStores?.length) return { status: "available" };
      const pointsCount = stockStores.length;
      const hasAvailable = stockStores.some((s) => s.quantity - s.reserved > 0);
      const hasUnavailable = stockStores.some(
        (s) => s.quantity - s.reserved <= 0,
      );
      if (hasAvailable && hasUnavailable && pointsCount > 1)
        return { status: "limited" };
      return { status: "available" };
    }

    if (!itemStops?.length) return { status: "available" };
    const pointsCount = itemStops.length;
    const hasAvailable = itemStops.some((stop) => !stop.isStopped);
    const hasUnavailable = itemStops.some((stop) => stop.isStopped);
    if (hasAvailable && hasUnavailable && pointsCount > 1)
      return { status: "limited" };
    return { status: "available" };
  }, [stockStores, stockEnabled, itemStops, forceStore, selectedStoreIds]);

  // Без forceStore показываем только limited
  if (!forceStore) {
    if (status !== "limited") {
      return (
        <div
          className={`${styles.container} ${small ? styles.containerSmall : ""}`}
        />
      );
    }

    const Icon = MdWarning;
    const text = small ? "Не везде" : "Доступен не везде";
    return (
      <div
        className={`${styles.container} ${small ? styles.containerSmall : ""}`}
      >
        <Icon size={small ? 12 : 16} color={theme.warning} />
        <span
          className={`${styles.text} ${small ? styles.textSmall : ""}`}
          style={{ color: theme.textSecondary }}
        >
          {text}
        </span>
      </div>
    );
  }

  // forceStore
  if (status === "available") {
    return (
      <div
        className={`${styles.container} ${small ? styles.containerSmall : ""}`}
      />
    );
  }

  if (status === "unavailable") {
    const Icon = MdRemoveShoppingCart;
    const text = small ? "Нет" : "Недоступен";
    return (
      <div
        className={`${styles.container} ${small ? styles.containerSmall : ""}`}
      >
        <Icon size={small ? 12 : 16} color={theme.danger} />
        <span
          className={`${styles.text} ${small ? styles.textSmall : ""}`}
          style={{ color: theme.textSecondary }}
        >
          {text}
        </span>
      </div>
    );
  }

  return (
    <div
      className={`${styles.container} ${small ? styles.containerSmall : ""}`}
    />
  );
};
